import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Euro, Layers } from 'lucide-react';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { useTariffTypeLookup } from '@/hooks/useTariffTypeLookup';
import { DeleteCellRenderer } from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { TariffDetails } from './tariff-details';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@/components/ui/tooltip';
import {
	TARIFFS_LIST_QUERY,
	TARIFF_QUERY,
	TARIFF_DELETE_MUTATION
} from './tariffs.gql.tsx';
import type { TariffsQuery, TariffQuery } from '@/graphql/generated';

type TariffListNode = NonNullable<
	NonNullable<TariffsQuery['tariffs']>['nodes']
>[number];

interface TariffsListResponse {
	tariffs?: {
		nodes?: TariffListNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

// Helper component to avoid lint warnings
const TariffTypeIconCell = ({
	params,
	getTariffTypeDescription,
	isFlatRateTariff
}: {
	params: ICellRendererParams<TariffListNode>;
	getTariffTypeDescription: (type: any) => string;
	isFlatRateTariff: (type: any) => boolean;
}) => {
	const tariff = params.data;
	const isFlatRate = isFlatRateTariff(tariff?.tariffTypeEnum);

	return (
		<div className="flex items-center justify-center">
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						{isFlatRate ? (
							<Euro className="w-4 h-4" />
						) : (
							<Layers className="w-4 h-4" />
						)}
					</TooltipTrigger>
					<TooltipContent>
						<p>
							{getTariffTypeDescription(tariff?.tariffTypeEnum)}
						</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
};

const TariffEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Layers className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

// Create a wrapper component for the icon cell renderer
const createTariffTypeIconRenderer =
	(
		getTariffTypeDescription: (type: any) => string,
		isFlatRateTariff: (type: any) => boolean
	) =>
	(params: ICellRendererParams<TariffListNode>) => (
		<TariffTypeIconCell
			params={params}
			getTariffTypeDescription={getTariffTypeDescription}
			isFlatRateTariff={isFlatRateTariff}
		/>
	);

export const TariffsList = () => {
	const { t } = useTranslation('tariffs');
	const { tariffTypeOptions, getTariffTypeDescription, isFlatRateTariff } =
		useTariffTypeLookup();
	const { showDialog } = useConfirmationDialog();
	const { goToCreate, goToEdit } = useResourceNavigation('tariffs');
	const go = useGo();

	const [selectedTariff, setSelectedTariff] = useState<TariffListNode | null>(
		null
	);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const [selectedTariffType, setSelectedTariffType] = useState<string>('');
	const [localTariffs, setLocalTariffs] = useState<TariffListNode[]>([]);
	const [searchParams, setSearchParams] = useSearchParams();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading: isLoading, refetch, fetchMore } =
		useQuery<TariffsListResponse>(TARIFFS_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.tariffs?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.tariffs?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.tariffs) return prev;

					return {
						...prev,
						tariffs: {
							...fetchMoreResult.tariffs,
							nodes: [
								...(prev.tariffs?.nodes ?? []),
								...(fetchMoreResult.tariffs.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local tariffs to use server data after fetchMore
			setLocalTariffs([]);
		} catch (error) {
			console.error('Error loading more tariffs:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const tariffsData = data?.tariffs?.nodes ?? [];

	const handleRefresh = () => {
		setLocalTariffs([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	// Lazy query for fetching individual tariff after success
	const [fetchTariff] = useLazyQuery<TariffQuery>(TARIFF_QUERY);

	// Delete mutation
	const [deleteTariff, { loading: deleteLoading }] = useMutation(
		TARIFF_DELETE_MUTATION
	);

	// Combined data: server data + local modifications
	const allTariffs = useMemo(() => {
		const serverTariffs = tariffsData || [];

		// Always prefer server data when empty - local state is only for optimistic updates
		if (localTariffs.length === 0 || serverTariffs.length === 0) {
			return serverTariffs;
		}

		// Merge local tariffs with server data
		const mergedTariffs = [...serverTariffs];

		for (const localTariff of localTariffs) {
			const existingIndex = mergedTariffs.findIndex(
				tariff => tariff.tariffId === localTariff.tariffId
			);

			if (existingIndex >= 0) {
				// Replace existing (for edits)
				mergedTariffs[existingIndex] = localTariff;
			} else {
				// Add new (for creates)
				mergedTariffs.unshift(localTariff);
			}
		}

		return mergedTariffs;
	}, [tariffsData, localTariffs]);

	// Filter tariffs based on selected tariff type
	const filteredTariffsData = selectedTariffType
		? allTariffs?.filter(
				(tariff: TariffListNode) =>
					tariff.tariffTypeEnum === selectedTariffType
			)
		: allTariffs;

	// Handle success from create/edit operations
	const handleTariffSuccess = useCallback(
		async (tariffId: string) => {
			try {
				// Fetch the updated/created tariff from server
				const { data: tariffData } = await fetchTariff({
					variables: { id: tariffId },
					fetchPolicy: 'network-only' // Always fetch fresh data
				});

				const tariffFromServer = tariffData?.tariff;
				if (!tariffFromServer) {
					console.warn('Could not fetch tariff data after success');
					return;
				}

				// Map the detailed tariff data to list format
				const listTariff: TariffListNode = {
					tariffId: tariffFromServer.tariffId,
					name: tariffFromServer.name,
					tariffTypeEnum: tariffFromServer.tariffTypeEnum,
					flatRateAmount: tariffFromServer.flatRateAmount
				};

				// Update local tariffs
				setLocalTariffs(prevLocal => {
					const existingIndex = prevLocal.findIndex(
						tariff => tariff.tariffId === tariffId
					);

					if (existingIndex >= 0) {
						// Replace existing tariff (edit case)
						const updated = [...prevLocal];
						updated[existingIndex] = listTariff;
						return updated;
					} else {
						// Add new tariff (create case)
						return [listTariff, ...prevLocal];
					}
				});

				// Select and highlight the tariff
				setSelectedTariff(listTariff);
			} catch (error) {
				console.error('Error fetching tariff after success:', error);
			}
		},
		[fetchTariff]
	);

	// URL parameter handling for success operations
	useEffect(() => {
		const successTariffId = searchParams.get('success');

		if (successTariffId) {
			// Clear the URL parameters
			setSearchParams(params => {
				params.delete('success');
				params.delete('mode');
				return params;
			});

			// Handle the success
			handleTariffSuccess(successTariffId);
		}
	}, [searchParams, setSearchParams, handleTariffSuccess]);

	const handleRowClick = (tariff: TariffListNode) => {
		setSelectedTariff(tariff);
	};

	// Helper function to find the next tariff to select after deletion
	const findNextTariffToSelect = (
		deletedTariff: TariffListNode,
		tariffList: TariffListNode[]
	) => {
		const currentIndex = tariffList.findIndex(
			t => t.tariffId === deletedTariff.tariffId
		);

		if (tariffList.length <= 1) {
			return null;
		}

		// Try to select the next tariff (same index after deletion)
		if (currentIndex < tariffList.length - 1) {
			return tariffList[currentIndex + 1];
		}

		// If deleting the last tariff, select the previous one
		if (currentIndex > 0) {
			return tariffList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextTariffToSelect: TariffListNode | null,
		updatedTariffs: TariffListNode[]
	) => {
		setLocalTariffs([]);
		// First, clear the current selection completely
		setSelectedTariff(null);

		// Then after a brief delay, set the next tariff
		setTimeout(() => {
			if (nextTariffToSelect && updatedTariffs.length > 0) {
				// Find the tariff in the updated list by ID
				const updatedTariff = updatedTariffs.find(
					t => t.tariffId === nextTariffToSelect?.tariffId
				);

				if (updatedTariff) {
					setSelectedTariff(updatedTariff);
				}
			}
		}, 100);
	};

	const handleDeleteTariff = async (tariff: TariffListNode) => {
		const confirmed = await showDialog(
			'Delete Tariff',
			`Are you sure you want to delete tariff "${tariff.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next tariff to select
			const wasSelectedTariff =
				selectedTariff?.tariffId === tariff.tariffId;

			// Clear selection immediately if deleting the selected tariff
			if (wasSelectedTariff) {
				setSelectedTariff(null);
			}

			try {
				const result = await deleteTariff({
					variables: {
						input: {
							tariffId: tariff.tariffId
						}
					},
					refetchQueries: ['Tariffs']
				});

				const checkResult = result.data?.deleteTariff;

				if (checkResult?.success) {
					showMutationSuccessToast('tariffs', 'delete');

					// Find the next tariff to select after deletion (before refetch)
					let nextTariffToSelect = null;
					if (wasSelectedTariff) {
						nextTariffToSelect = findNextTariffToSelect(
							tariff,
							filteredTariffsData
						);
					}

					// Refetch data
					refetch();

					// Wait a bit for the refresh to complete, then handle selection
					setTimeout(() => {
						handlePostDeletionSelection(
							nextTariffToSelect,
							tariffsData || []
						);
					}, 200);
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete tariff: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete tariff error:', error);
				toast.error('Failed to delete tariff. Please try again.');
			}
		}
	};

	const handleDeleteTariffById = async (tariffId: string) => {
		const tariff = filteredTariffsData?.find(t => t.tariffId === tariffId);
		if (tariff) {
			await handleDeleteTariff(tariff);
		}
	};

	const handleCloneTariff = async (tariffId: string) => {
		try {
			// Fetch full tariff details including steps for cloning
			const { data } = await fetchTariff({
				variables: { id: tariffId },
				fetchPolicy: 'network-only'
			});

			const tariff = data?.tariff;
			if (!tariff) {
				toast.error(t('messages.cloneError'));
				return;
			}

			// Clone tariff steps (strip IDs so new ones are generated)
			const clonedSteps = (tariff.tariffSteps ?? []).map(step => ({
				stepSequence: step.stepSequence,
				intervalMinutes: step.intervalMinutes,
				intervalCharge: step.intervalCharge,
				maxIntervals: step.maxIntervals
			}));

			go({
				to: '/tariffs/create',
				state: {
					cloneData: {
						name: `${tariff.name} (Copy)`,
						tariffTypeEnum: tariff.tariffTypeEnum,
						flatRateAmount: tariff.flatRateAmount,
						tariffSteps: clonedSteps
					}
				}
			});
		} catch (error) {
			console.error('Failed to clone tariff:', error);
			toast.error(t('messages.cloneError'));
		}
	};

	const columns: ColDef<TariffListNode>[] = [
		{
			headerName: '',
			field: 'tariffTypeEnum',
			width: 35,
			maxWidth: 35,
			minWidth: 35,
			resizable: false,
			sortable: false,
			cellRenderer: createTariffTypeIconRenderer(
				getTariffTypeDescription,
				isFlatRateTariff
			)
		},
		{
			headerName: t('list.columns.name'),
			field: 'name',
			flex: 1,
			minWidth: 200
		},
		{
			headerName: t('list.columns.flatRate'),
			field: 'flatRateAmount',
			width: 140,
			minWidth: 140,
			cellClass: 'ag-right-aligned-cell',
			headerClass: 'ag-right-aligned-header',
			cellRenderer: (params: ICellRendererParams<TariffListNode>) => {
				const tariff = params.data;
				const value = tariff?.flatRateAmount;
				const isFlatRate = isFlatRateTariff(tariff?.tariffTypeEnum);

				if (isFlatRate && value != null && Number(value) > 0) {
					const formatted = Number(value).toFixed(2);
					return `${formatted}`;
				}

				return '-';
			}
		},
		{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteTariff,
				disabled: deleteLoading
			},
			sortable: false,
			filter: false,
			resizable: false
		}
	];

		return (
		<ListPageLayout<TariffListNode>
			columns={columns}
			data={filteredTariffsData}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			selectedItem={selectedTariff}
			detailsTitle="Tariff Details"
			onEdit={goToEdit}
			onDelete={handleDeleteTariffById}
			onClone={handleCloneTariff}
			onOpenInNewWindow={id => openEntityInNewWindow('tariffs', id)}
			renderDetailsContent={tariff => (
				<TariffDetails
					tariffId={tariff.tariffId}
					activeTab={activeDetailsTab}
					onTabChange={setActiveDetailsTab}
				/>
			)}
			getRecordId={tariff => tariff.tariffId}
			getDetailsDescription={tariff => tariff.name}
			renderEntityIcon={() => <TariffEntityIcon />}
			getEntityTypeName={tariff =>
				isFlatRateTariff(tariff.tariffTypeEnum) ? t('tariffType.flatRate.title') : t('tariffType.tiered.title')
			}
			showCreateButton
			showRefreshButton
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
			renderCustomFilters={() => (
				<div className="w-48">
					<SentryDropdown
						options={tariffTypeOptions}
						value={selectedTariffType}
						onChange={value =>
							setSelectedTariffType(value as string)
						}
						placeholder="All Tariff Types"
						groupByCategory={false}
						className="w-full"
						variant='transparent'
					/>
				</div>
			)}
		/>
	);
};
